import { createContext, useContext, useEffect, useReducer, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { apiFetch } from '../lib/api';

/** Authenticated dashboard user. Belongs to one organization, has one role. */
export interface User {
  id: number;
  email: string;
  orgId: number;
  orgName: string;
  role: 'owner' | 'admin' | 'member';
  /** Member-only schema allowlist. Empty for owner/admin (unrestricted). */
  schemas: string[];
}

interface AuthState {
  user: User | null;
  sessionToken: string | null;
  isLoading: boolean;
}

type Action =
  | { type: 'LOGIN'; user: User; sessionToken: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; isLoading: boolean };

const initialState: AuthState = { user: null, sessionToken: null, isLoading: true };

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case 'LOGIN':
      return { user: action.user, sessionToken: action.sessionToken, isLoading: false };
    case 'LOGOUT':
      return { user: null, sessionToken: null, isLoading: false };
    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };
  }
}

/**
 * Session token storage. Server TTL is 14 days; browser storage is limited
 * to the current tab and cleared on logout. The dashboard never stores plaintext API keys.
 */
const SESSION_TOKEN_KEY = 'astdz_session_token';
const INACTIVITY_MS = 30 * 60 * 1000;

function readSessionToken(): string | null {
  try {
    // Retire the previous persistent browser token rather than retaining a
    // bearer credential after the user closes their tab.
    localStorage.removeItem(SESSION_TOKEN_KEY);
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch { return null; }
}
function writeSessionToken(t: string) {
  try { sessionStorage.setItem(SESSION_TOKEN_KEY, t); } catch { /* ignore */ }
}
function clearSessionToken() {
  try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
}

interface AuthContextValue {
  user: User | null;
  sessionToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signupComplete: (user: User, sessionToken: string) => void;
  logout: () => void;
  isAdmin: boolean;
  /** True iff the current user can act on `schema` (owner/admin or member with allowlist match). */
  canUseSchema: (schema: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  userId: number;
  orgId: number;
  role: 'owner' | 'admin' | 'member';
  email: string;
  orgName: string;
  sessionToken: string;
  expiresAt: number;
}

interface MeResponse {
  userId: number;
  orgId: number;
  email: string;
  role: 'owner' | 'admin' | 'member';
  orgName: string;
  userSchemas: string[];
  sessionExpiresAt: number;
}

/**
 * Provides auth state and actions to the tree.
 *
 * Hydrates from a session token in sessionStorage by calling GET /v1/auth/me.
 * If the token is missing or invalid the user is sent to /login. There is no
 * client-side API key state — keys are minted and copied via the Keys page,
 * never cached in the dashboard.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const timerRef = useRef<number | null>(null);

  const logout = useCallback(() => {
    const token = readSessionToken();
    clearSessionToken();
    dispatch({ type: 'LOGOUT' });
    // Best-effort server-side revocation; ignore failures.
    if (token) {
      void apiFetch('/v1/auth/logout', {
        method: 'POST', body: {}, authToken: token, keepalive: true, skipUnauthorizedHandler: true,
      }).catch(() => { /* local logout still completes if offline */ });
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => logout(), INACTIVITY_MS);
  }, [logout]);

  // Hydrate from session token: call /me to fetch current user identity.
  useEffect(() => {
    const token = readSessionToken();
    if (!token) {
      dispatch({ type: 'SET_LOADING', isLoading: false });
      return;
    }
    let disposed = false;
    apiFetch<MeResponse>('/v1/auth/me', { authToken: token })
      .then((me) => {
        if (disposed || readSessionToken() !== token) return;
        const user: User = {
          id: me.userId,
          email: me.email,
          orgId: me.orgId,
          orgName: me.orgName,
          role: me.role,
          schemas: me.userSchemas,
        };
        dispatch({ type: 'LOGIN', user, sessionToken: token });
      })
      .catch(() => {
        if (disposed || readSessionToken() !== token) return;
        clearSessionToken();
        dispatch({ type: 'SET_LOADING', isLoading: false });
      });
    return () => { disposed = true; };
  }, []);

  // Inactivity timer + 401 listener
  useEffect(() => {
    if (!state.user) return;
    resetTimer();
    const events: Array<keyof DocumentEventMap> = ['mousemove', 'keypress', 'click'];
    events.forEach((e) => document.addEventListener(e, resetTimer));
    const onUnauth = () => logout();
    window.addEventListener('astdz:unauthorized', onUnauth);
    return () => {
      events.forEach((e) => document.removeEventListener(e, resetTimer));
      window.removeEventListener('astdz:unauthorized', onUnauth);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [state.user, resetTimer, logout]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<LoginResponse>('/v1/auth/login', { body: { email, password }, skipAuth: true });
    writeSessionToken(res.sessionToken);
    // /me also returns userSchemas; for member-role users that's required.
    // Fetch it once so the user object is complete.
    const me = await apiFetch<MeResponse>('/v1/auth/me', { authToken: res.sessionToken });
    if (readSessionToken() !== res.sessionToken) return;
    const user: User = {
      id: me.userId,
      email: me.email,
      orgId: me.orgId,
      orgName: me.orgName,
      role: me.role,
      schemas: me.userSchemas,
    };
    dispatch({ type: 'LOGIN', user, sessionToken: res.sessionToken });
  }, []);

  const signupComplete = useCallback((user: User, sessionToken: string) => {
    writeSessionToken(sessionToken);
    dispatch({ type: 'LOGIN', user, sessionToken });
  }, []);

  const isAdmin = state.user?.role === 'owner' || state.user?.role === 'admin';
  const canUseSchema = useCallback(
    (schema: string): boolean => {
      if (!state.user) return false;
      if (state.user.role !== 'member') return true;
      return state.user.schemas.includes(schema);
    },
    [state.user]
  );

  const value: AuthContextValue = {
    user: state.user,
    sessionToken: state.sessionToken,
    isLoading: state.isLoading,
    isAuthenticated: state.user !== null,
    login,
    signupComplete,
    logout,
    isAdmin,
    canUseSchema,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context. Throws if used outside `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
